import { useEffect, useRef, useState } from 'react';

export const CAMPAIGN_IMAGE_REVEAL_ROOT_MARGIN = '0px 0px -8% 0px';
export const CAMPAIGN_IMAGE_REVEAL_THRESHOLD = 0.01;

function isElementInViewport(element: Element): boolean {
  const rect = element.getBoundingClientRect();

  return (
    rect.bottom > 0 &&
    rect.top < window.innerHeight &&
    rect.right > 0 &&
    rect.left < window.innerWidth
  );
}

/**
 * Observes an element and calls onReveal once when it enters the viewport
 * Returns a cleanup function when observation is active
 */
export function observeCampaignCardImageReveal(
  element: Element,
  onReveal: () => void,
  onReset: () => void
): (() => void) | undefined {
  if (typeof IntersectionObserver === 'undefined') {
    onReveal();
    return undefined;
  }

  if (isElementInViewport(element)) {
    onReveal();
    return undefined;
  }

  onReset();

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) {
        return;
      }

      onReveal();
      observer.disconnect();
    },
    {
      rootMargin: CAMPAIGN_IMAGE_REVEAL_ROOT_MARGIN,
      threshold: CAMPAIGN_IMAGE_REVEAL_THRESHOLD,
    }
  );

  observer.observe(element);

  return () => observer.disconnect();
}

/**
 * Fades in a campaign card image container when it scrolls into view.
 * Re-runs when revealKey changes (e.g. different campaign or image)
 */
export function useCampaignCardImageReveal(revealKey: string) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return;
    }

    return observeCampaignCardImageReveal(
      element,
      () => setRevealed(true),
      () => setRevealed(false)
    );
  }, [revealKey]);

  return { containerRef, revealed };
}
